import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './McScreen.css';

/* The MC's screen — the person on stage with the microphone, on a laptop at the mixing desk.

   ★★ No login. The token in the address is the key; Machina (Event ▸ Engagement ▸ The Vibe) turns
   it on, rotates it and turns it off. One link covers the token's event AND the other event on the
   same day at the same venue (HT + PLG Dubai at The Sevens), so the timer always shows both.

   Four tiles: the TIMER (a jingle three minutes before every round ends — marshalls wrap up,
   resting players move on), the PLANNING (where everybody is, which moment comes next), THE VIBE
   (storylines to read out) and the TOP 5.

   ★ Browsers refuse to play sound before somebody touches the page, so the screen opens on a
   "Start MC mode" button: that tap unlocks the audio and keeps the laptop awake.
   ★ Played jingles are remembered in localStorage, so reloading never replays one, and a jingle
   whose moment passed more than CUE_WINDOW_MS ago is never played late.
   ★ Scores carry no timestamp: a story is NEW when this screen had not seen it before.
   ★ The server's clock wins: every poll measures the skew and the tick corrects for it. */

const POLL_MS = 20000;
const TICK_MS = 500;
const CUE_WINDOW_MS = 30000;
const JINGLE_REPEATS = 3;                             // one cue plays the jingle three times in a row
const DUE_MS = 60000;                                 // how long the timer shouts "jingle NOW", sound or not
const NEW_FOR_MS = 10 * 60 * 1000;

const store = {
  get(key, fallback) {
    try { const v = window.localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* private window */ }
  },
};

const cssUrl = (image) => (typeof image === 'string' && image.includes(' ') ? encodeURI(image) : image);

const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const clock = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};

const ordinal = (n, lang) => {
  if (n === null || n === undefined) return '';
  const l = (lang || 'en').slice(0, 2);
  if (l === 'nl') return `${n}e`;
  if (l === 'fr') return n === 1 ? '1er' : `${n}e`;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
};

const localTime = (ms, tz) => {
  try {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz || undefined }).format(new Date(ms));
  } catch {
    return new Date(ms).toTimeString().slice(0, 5);
  }
};

const Face = ({ url, name, className }) => (url
  ? <span className={className} style={{ backgroundImage: `url("${cssUrl(url)}")` }} />
  : <span className={`${className} mc-face--initials`}>{(name || '?').trim().slice(0, 1).toUpperCase()}</span>);

const McScreen = () => {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [gone, setGone] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [started, setStarted] = useState(false);
  const [audioOk, setAudioOk] = useState(false);
  const [muted, setMuted] = useState(() => store.get(`mc-muted:${token}`, false));
  const [active, setActive] = useState(0);
  const [said, setSaid] = useState(() => store.get(`mc-said:${token}`, {}));
  const [playing, setPlaying] = useState(null);       // the cue labels the jingle is playing for
  const skew = useRef(0);
  const audio = useRef(null);
  const repeatsLeft = useRef(0);
  const seen = useRef(store.get(`mc-seen:${token}`, {}));
  const firstLoad = useRef(true);
  const played = useRef(store.get(`mc-played:${token}`, {}));
  const wakeLock = useRef(null);
  const lang = i18n.language;

  // ---------------------------------------------------------------- data
  const load = useCallback(() => {
    apiRequest('GET', `/public/mc/${token}`)
      .then((res) => {
        if (res.server_now) skew.current = res.server_now - Date.now();
        const at = Date.now();
        const map = seen.current;
        (res.events || []).forEach((ev) => [...(ev.stories || []), ...(ev.extra || [])].forEach((s) => {
          // The first load marks everything as known: only what arrives WHILE the MC watches is new.
          if (!(s.key in map)) map[s.key] = firstLoad.current ? 0 : at;
        }));
        firstLoad.current = false;
        store.set(`mc-seen:${token}`, map);
        setData(res);
        setGone(false);
      })
      // ★ A failed poll keeps the screen as it is; only a link that never loaded says it is gone.
      .catch(() => setGone(true));
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() + skew.current), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // ---------------------------------------------------------------- sound
  const playJingle = useCallback((labels) => {
    const el = audio.current;
    if (!el) return;
    el.currentTime = 0;
    el.muted = false;
    el.volume = 1;
    repeatsLeft.current = JINGLE_REPEATS - 1;
    setPlaying(labels || []);
    el.play().then(() => setAudioOk(true)).catch(() => setAudioOk(false));
  }, []);

  const onJingleEnded = useCallback(() => {
    const el = audio.current;
    if (el && repeatsLeft.current > 0) {
      repeatsLeft.current -= 1;
      el.currentTime = 0;
      el.play().catch(() => setPlaying(null));
      return;
    }
    setPlaying(null);
  }, []);

  // ★ The browser fires 'pause' at the end of every play too; only a real stop ends the repeats.
  const onJinglePause = useCallback(() => {
    if (audio.current && audio.current.ended) return;
    repeatsLeft.current = 0;
    setPlaying(null);
  }, []);

  const start = useCallback(() => {
    const el = audio.current;
    if (el && el.src) {
      // One silent play inside the tap is what lets the browser play it later on its own.
      el.muted = true;
      el.play().then(() => { el.pause(); el.currentTime = 0; el.muted = false; setAudioOk(true); })
        .catch(() => setAudioOk(false));
    }
    if (navigator.wakeLock && navigator.wakeLock.request) {
      navigator.wakeLock.request('screen').then((l) => { wakeLock.current = l; }).catch(() => {});
    }
    setStarted(true);
  }, []);

  // The wake lock drops when the tab is hidden; take it again when the MC comes back.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && started && navigator.wakeLock && navigator.wakeLock.request) {
        navigator.wakeLock.request('screen').then((l) => { wakeLock.current = l; }).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [started]);

  const toggleMute = () => {
    setMuted((m) => { store.set(`mc-muted:${token}`, !m); return !m; });
  };

  const cues = useMemo(() => data?.cues || [], [data]);
  const nextCue = useMemo(
    () => cues.find((c) => c.at_ms + CUE_WINDOW_MS > now && !c.keys.every((k) => played.current[k])),
    [cues, now],
  );

  // The cue whose moment is now, played or not: muted or blocked, the MC still sees it is time.
  const dueCue = useMemo(
    () => [...cues].reverse().find((c) => c.at_ms <= now && now < c.at_ms + DUE_MS),
    [cues, now],
  );

  // Fire a cue once, inside its window.
  useEffect(() => {
    if (!started || !nextCue) return;
    if (now < nextCue.at_ms || now > nextCue.at_ms + CUE_WINDOW_MS) return;
    nextCue.keys.forEach((k) => { played.current[k] = now; });
    store.set(`mc-played:${token}`, played.current);
    if (!muted) playJingle(nextCue.labels);
  }, [now, nextCue, started, muted, playJingle, token]);

  // ---------------------------------------------------------------- keys
  const toggleFull = useCallback(() => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
    } else if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    }
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (e.key === 'f' || e.key === 'F') toggleFull();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleFull]);

  const markSaid = (key) => {
    setSaid((s) => {
      const next = { ...s };
      if (next[key]) delete next[key]; else next[key] = Date.now();
      store.set(`mc-said:${token}`, next);
      return next;
    });
  };

  // ---------------------------------------------------------------- derived
  const events = useMemo(() => data?.events || [], [data]);
  const event = events[Math.min(active, Math.max(events.length - 1, 0))];
  const tz = events[0]?.time_zone;
  const roundLabel = useCallback((round, group, short) => {
    const base = t('mcRound', { n: round });
    return [short, base, group ? t('mcGroup', { group }) : null].filter(Boolean).join(' · ');
  }, [t]);

  const cueText = (labels) => labels
    .map((l) => t('mcCueFor', { round: roundLabel(l.round, l.group, events.length > 1 ? l.event : null), time: l.ends }))
    .join(' + ');

  const lineName = useCallback((line, short) => (
    line.kind === 'round' ? roundLabel(line.round, line.group, short) : (line.name || t('mcMoment'))
  ), [roundLabel, t]);

  // Per event: the rounds in play, the next round, the moment running now and the next moment.
  const status = useMemo(() => events.map((ev) => {
    const lines = ev.lines || [];
    const rounds = lines.filter((l) => l.kind === 'round');
    const moments = lines.filter((l) => l.kind === 'moment');
    const inPlay = rounds.filter((l) => l.start_ms <= now && l.end_ms && now < l.end_ms);
    const nextRound = rounds.find((l) => l.start_ms > now);
    const momentNow = moments.find((l) => l.start_ms <= now && l.end_ms && now < l.end_ms);
    const nextMoment = moments.find((l) => l.start_ms > now);
    const totalRounds = rounds.reduce((m, l) => Math.max(m, l.round || 0), 0);
    const over = lines.length > 0 && lines.every((l) => (l.end_ms || l.start_ms) <= now);
    return { ev, inPlay, nextRound, momentNow, nextMoment, totalRounds, over };
  }), [events, now]);

  // ---------------------------------------------------------------- the vibe, worded
  const teamName = (ev, id) => ev?.teams?.[id]?.name || '';

  const wordStory = (ev, s) => {
    const team = teamName(ev, s.team);
    const lines = [];
    let headline = '';
    const vars = (extra = {}) => ({ team, ...extra, interpolation: { escapeValue: false } });
    switch (s.type) {
      case 'result': {
        if (s.place === 1) headline = t(`mcResultWin_${hash(s.key) % 3}`, vars({ game: s.game || t('mcThisGame') }));
        else if (s.dq) headline = t('mcResultDq', vars({ game: s.game || t('mcThisGame') }));
        else headline = t('mcResultPlace', vars({ place: ordinal(s.place, lang), game: s.game || t('mcThisGame') }));
        if (s.joker) {
          lines.push(s.joker_pts ? t('mcJokerPoints', vars({ points: s.joker_pts })) : t('mcJokerPlayed', vars()));
        }
        (s.beaten || []).forEach((b) => {
          lines.push(t(b.relation === 'they_challenged' ? 'mcBeatChallenger' : 'mcBeatChallenged',
            vars({ rival: teamName(ev, b.team) })));
        });
        if (s.rank_before && s.rank_after && s.rank_after < s.rank_before) {
          lines.push(t('mcClimb', vars({ from: ordinal(s.rank_before, lang), to: ordinal(s.rank_after, lang) })));
        } else if (s.rank_after) {
          lines.push(t('mcNowPlace', vars({ place: ordinal(s.rank_after, lang) })));
        }
        break;
      }
      case 'rival_overtake':
        headline = t(s.relation === 'they_challenged' ? 'mcOvertakeChallenger' : 'mcOvertakeChallenged',
          vars({ rival: teamName(ev, s.rival), place: ordinal(s.rank_after, lang) }));
        break;
      case 'leader':
        headline = t('mcNewLeader', vars({ points: s.points }));
        break;
      case 'mission_live':
        headline = t(s.kind === 'video' ? 'mcMissionLiveVideo' : 'mcMissionLivePhoto',
          vars({ mission: s.mission, points: s.points }));
        if (s.ends) lines.push(t('mcMissionCloses', vars({ time: s.ends })));
        break;
      case 'mission_in':
        headline = t(s.approved ? 'mcMissionInApproved' : 'mcMissionIn', vars({ mission: s.mission }));
        break;
      case 'intro':
        headline = t(`mcIntro_${hash(s.key) % 3}`, vars());
        break;
      default:
        headline = team;
    }
    return { headline, lines };
  };

  const vibeItems = useMemo(() => {
    if (!event) return [];
    const all = [...(event.stories || []), ...(event.extra || [])]
      .filter((s) => !s.team || event.teams?.[s.team]);
    const at = Date.now();
    const isNew = (s) => (seen.current[s.key] || 0) > at - NEW_FOR_MS;
    const rank = (s) => (said[s.key] ? 2 : (isNew(s) ? 0 : 1));
    return all.map((s, i) => ({ s, i, fresh: isNew(s) }))
      .sort((a, b) => rank(a.s) - rank(b.s) || a.i - b.i);
    // `now` re-sorts as NEW badges age out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, said, now]);

  // ---------------------------------------------------------------- render
  if (!data) {
    return (
      <div className="mc mc--empty">
        <p className="mc__note">{gone ? t('mcGone') : t('pleaseWait')}</p>
      </div>
    );
  }

  const ownStatus = status[Math.min(active, status.length - 1)];
  const wallBehind = event && event.wall_quarter !== null && event.wall_quarter !== undefined
    && event.rounds_scored > event.wall_quarter;

  // Congratulations after a result; an introduction just names them.
  const playersLine = (team, type) => {
    const names = (team?.players || []).map((p) => p.first_name).filter(Boolean);
    if (!names.length || names.length > 10) return null;
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} ${t('mcAnd')} ${names[names.length - 1]}`;
    return t(type === 'intro' ? 'mcInTeam' : 'mcWellDone', { names: list, interpolation: { escapeValue: false } });
  };

  return (
    <div className="mc">
      <audio ref={audio} src={data.jingle_url || undefined} preload="auto"
             onEnded={onJingleEnded} onPause={onJinglePause} />

      {!started && (
        <div className="mc-start">
          <div className="mc-start__card">
            <Icon name="microphone" theme="light" className="mc-start__icon" />
            <h1>{t('mcTitle')}</h1>
            <p>{events.map((e) => e.name).join(' + ')}</p>
            <button type="button" className="mc-btn mc-btn--big" onClick={start}>{t('mcStart')}</button>
            <p className="mc-start__hint">{data.jingle_url ? t('mcStartHint') : t('mcNoJingle')}</p>
          </div>
        </div>
      )}

      <header className="mc__head">
        <span className="mc__title"><Icon name="microphone" theme="light" /> {t('mcTitle')}</span>
        {events.length > 1 && (
          <span className="mc-tabs">
            {events.map((e, i) => (
              <button key={e.id} type="button" title={e.name}
                      className={`mc-tab${i === active ? ' mc-tab--on' : ''}`}
                      style={e.color ? { '--mc-tab': e.color } : undefined}
                      onClick={() => setActive(i)}>{e.short}</button>
            ))}
          </span>
        )}
        <span className="mc__clock">{localTime(now, tz)}</span>
        <button type="button" className={`mc-btn${muted ? ' mc-btn--warn' : ''}`} onClick={toggleMute}>
          {muted ? t('mcUnmute') : t('mcMute')}
        </button>
        <button type="button" className="mc-btn mc-btn--icon" onClick={toggleFull} title={t('rankingScreenFullscreen')}>
          <Icon name="expand" theme="light" />
        </button>
      </header>

      {started && data.jingle_url && !audioOk && (
        <div className="mc-banner" role="alert">
          {t('mcAudioLocked')} <button type="button" className="mc-btn" onClick={start}>{t('mcStart')}</button>
        </div>
      )}
      {!data.jingle_url && <div className="mc-banner" role="alert">{t('mcNoJingle')}</div>}

      <main className="mc__grid">
        {/* ------------------------------------------------ TIMER */}
        <section className={`mc-tile mc-timer${playing ? ' mc-timer--playing' : ''}${dueCue && !playing ? ' mc-timer--due' : ''}`}>
          <h2 className="mc-tile__title"><Icon name="clock" theme="light" /> {t('mcTimer')}</h2>
          {status.map(({ ev, inPlay, nextRound, totalRounds, over }) => (
            <div key={ev.id} className="mc-timer__event">
              {events.length > 1 && <span className="mc-timer__short">{ev.short}</span>}
              {inPlay.length > 0 ? inPlay.map((l) => (
                <div key={l.id} className="mc-timer__row">
                  <span className="mc-timer__label">
                    {roundLabel(l.round, l.group)}{totalRounds ? ` / ${totalRounds}` : ''}
                    <small>{t('mcUntil', { time: l.end })}</small>
                  </span>
                  <span className={`mc-timer__big${l.end_ms - now <= 3 * 60000 ? ' mc-timer__big--hot' : ''}`}>
                    {clock(l.end_ms - now)}
                  </span>
                </div>
              )) : (
                <div className="mc-timer__row">
                  <span className="mc-timer__label">
                    {over ? t('mcDayOver') : (nextRound
                      ? <>{t('mcBetweenRounds')}<small>{t('mcNextRoundAt', { round: roundLabel(nextRound.round, nextRound.group), time: nextRound.start })}</small></>
                      : t('mcNoRoundsLeft'))}
                  </span>
                  {nextRound && !over && <span className="mc-timer__big mc-timer__big--dim">{clock(nextRound.start_ms - now)}</span>}
                </div>
              )}
            </div>
          ))}
          <div className="mc-timer__jingle">
            {playing ? (
              <span className="mc-timer__now">
                <Icon name="bell" theme="light" /> {t('mcJinglePlaying')}
                {playing.length > 0 && <small>{cueText(playing)}</small>}
              </span>
            ) : dueCue ? (
              <span className="mc-timer__now mc-timer__now--due">
                <Icon name="bell" theme="light" /> {t('mcJingleNow')}
                <small>{cueText(dueCue.labels)}</small>
              </span>
            ) : nextCue ? (
              <span>
                {t('mcNextJingle')} <b>{clock(Math.max(0, nextCue.at_ms - now))}</b>
                <small>{cueText(nextCue.labels)}</small>
              </span>
            ) : <span>{t('mcNoJinglesLeft')}</span>}
            <span className="mc-timer__btns">
              <button type="button" className={`mc-btn${dueCue && !playing ? ' mc-btn--big' : ''}`} disabled={!data.jingle_url}
                      onClick={() => playJingle(dueCue && !playing ? dueCue.labels : [])}>
                <Icon name="play" theme="light" /> {t('mcPlayNow')}
              </button>
              {playing && (
                <button type="button" className="mc-btn" onClick={() => { if (audio.current) audio.current.pause(); }}>
                  {t('mcStop')}
                </button>
              )}
            </span>
          </div>
        </section>

        {/* ------------------------------------------------ PLANNING */}
        <section className="mc-tile mc-plan">
          <h2 className="mc-tile__title"><Icon name="calendar" theme="light" /> {t('mcPlanning')}
            {events.length > 1 && <span className="mc-tile__tag">{event?.short}</span>}</h2>
          {ownStatus && (
            <div className="mc-plan__focus">
              {ownStatus.momentNow && (
                <p className="mc-plan__now">{t('mcNowMoment')} <b>{ownStatus.momentNow.name}</b> <small>{t('mcUntil', { time: ownStatus.momentNow.end })}</small></p>
              )}
              {ownStatus.nextMoment ? (
                <p className="mc-plan__next">
                  {t('mcNextMoment')} <b>{ownStatus.nextMoment.name}</b> {t('mcAt', { time: ownStatus.nextMoment.start })}
                  <span className="mc-plan__in">{t('mcIn', { time: clock(ownStatus.nextMoment.start_ms - now) })}</span>
                </p>
              ) : <p className="mc-plan__next">{t('mcNoMomentsLeft')}</p>}
            </div>
          )}
          <ol className="mc-plan__list">
            {(event?.lines || []).length === 0 && <li className="mc-plan__empty">{t('mcNoPlanning')}</li>}
            {(event?.lines || []).map((l) => {
              const past = (l.end_ms || l.start_ms) <= now;
              const live = l.start_ms <= now && l.end_ms && now < l.end_ms;
              return (
                <li key={l.id} className={`mc-plan__line mc-plan__line--${l.kind}${past ? ' is-past' : ''}${live ? ' is-live' : ''}`}>
                  <span className="mc-plan__time">{l.start}{l.end ? `–${l.end}` : ''}</span>
                  <span className="mc-plan__name">{lineName(l)}</span>
                </li>
              );
            })}
          </ol>
        </section>

        {/* ------------------------------------------------ THE VIBE */}
        <section className="mc-tile mc-vibe">
          <h2 className="mc-tile__title"><Icon name="megaphone" theme="light" /> {t('mcVibe')}
            {events.length > 1 && <span className="mc-tile__tag">{event?.short}</span>}</h2>
          {vibeItems.length === 0 && <p className="mc__note">{t('mcVibeEmpty')}</p>}
          <ul className="mc-vibe__list">
            {vibeItems.map(({ s, fresh }) => {
              const team = s.team ? event.teams[s.team] : null;
              const { headline, lines } = wordStory(event, s);
              const showTeam = team && ['result', 'rival_overtake', 'leader', 'intro', 'mission_in'].includes(s.type);
              const song = team?.song;
              const songUrl = song?.spotify_url || song?.youtube_url;
              const done = !!said[s.key];
              return (
                <li key={s.key} className={`mc-card mc-card--${s.type}${done ? ' is-said' : ''}`}>
                  {showTeam && <Face url={team.photo_url} name={team.name} className="mc-card__face" />}
                  <div className="mc-card__body">
                    <p className="mc-card__meta">
                      {fresh && !done && <span className="mc-pill mc-pill--new">{t('mcNew')}</span>}
                      {s.round ? <span className="mc-pill">{t('mcRound', { n: s.round })}</span> : null}
                      {s.beyond_wall && <span className="mc-pill mc-pill--warn">{t('mcNotOnWall')}</span>}
                      {showTeam && <b className="mc-card__team">{team.name}</b>}
                    </p>
                    <p className="mc-card__headline">{headline}</p>
                    {lines.map((line, i) => <p key={i} className="mc-card__line">{line}</p>)}
                    {showTeam && (
                      <>
                        {['result', 'leader', 'intro'].includes(s.type) && playersLine(team, s.type) && (
                          <p className="mc-card__line">{playersLine(team, s.type)}</p>
                        )}
                        {team.slogan && <p className="mc-card__slogan">“{team.slogan}”</p>}
                        {team.supporters > 0 && (
                          <p className="mc-card__line mc-card__line--dim">{t('mcSupporters', { count: team.supporters })}</p>
                        )}
                      </>
                    )}
                    <p className="mc-card__actions">
                      {showTeam && songUrl && (
                        <a className="mc-btn mc-btn--song" href={songUrl} target="_blank" rel="noopener noreferrer">
                          <Icon name="play" theme="light" /> {song.spotify_url ? t('mcSongSpotify') : t('mcSongYoutube')}
                          {song.title ? `: ${song.title}` : ''}
                        </a>
                      )}
                      <button type="button" className={`mc-btn${done ? ' mc-btn--done' : ''}`} onClick={() => markSaid(s.key)}>
                        <Icon name="check" theme="light" /> {done ? t('mcSaid') : t('mcMarkSaid')}
                      </button>
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {/* ------------------------------------------------ TOP 5 */}
        <section className="mc-tile mc-top">
          <h2 className="mc-tile__title"><Icon name="ranking-star" theme="light" /> {t('mcTop5')}
            {events.length > 1 && <span className="mc-tile__tag">{event?.short}</span>}</h2>
          {wallBehind && <p className="mc-pill mc-pill--warn mc-top__wall">{t('mcWallBehind', { round: event.wall_quarter })}</p>}
          <div className={`mc-top__cols${data.combined && data.combined.length ? ' mc-top__cols--two' : ''}`}>
            <div>
              {(event?.top || []).length === 0 ? <p className="mc__note">{t('mcTopEmpty')}</p> : (
                <ol className="mc-top__list">
                  {event.top.map((r) => (
                    <li key={r.team} className="mc-top__row">
                      <span className="mc-top__place">{r.place}</span>
                      <Face url={r.photo_url} name={r.name} className="mc-top__face" />
                      <span className="mc-top__name">{r.name}{r.company && r.company !== r.name ? <small>{r.company}</small> : null}</span>
                      <span className="mc-top__points">{r.points}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            {data.combined && data.combined.length > 0 && (
              <div>
                <h3 className="mc-top__sub">{t('mcCombined')}</h3>
                <ol className="mc-top__list mc-top__list--small">
                  {data.combined.map((r) => (
                    <li key={r.team} className="mc-top__row">
                      <span className="mc-top__place">{r.place}</span>
                      <span className="mc-top__name">{r.name}
                        <small>{(events.find((e) => e.id === r.event_id) || {}).short}</small></span>
                      <span className="mc-top__points">{r.points}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default McScreen;
